// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A visually engaging loading component for asset reports.
 *
 * Displays a checklist of analysis steps that get "checked off" sequentially
 * while waiting for the API response. Provides users with feedback that
 * analysis work is happening rather than showing a simple spinner.
 *
 * Timing is dynamically calculated based on the organization's asset count.
 * Each report type has separate timing constants and step labels.
 *
 * Steps complete at timed intervals proportional to expected load time,
 * and accelerate to completion once actual data arrives.
 */
import {
  type AssetReportType,
  ReportLoaderDisplay,
  assetReportConfigs,
  useReportLoaderProgress,
} from '@/report/components/report-loader';

interface Props {
  /** When true, accelerates remaining steps and completes quickly */
  isDataReady?: boolean;
  /** Called when all steps are complete */
  onComplete?: () => void;
  /** Type of report being loaded (affects step labels) */
  reportType: AssetReportType;
  /** Number of periods in the date range (for asset-period timing calculation) */
  periodCount?: number;
}

/**
 * Displays a pseudo-progress loader with a checklist of analysis steps.
 * Fetches asset count to calculate realistic step timing, then animates
 * through steps proportionally. Accelerates when actual data arrives.
 */
export function AssetReportLoader(props: Props) {
  const { isDataReady, onComplete, reportType, periodCount = 1 } = props;

  const config = assetReportConfigs[reportType];
  // All asset reports except fulfillment scale timing by asset-periods
  const useAssetPeriods = reportType !== 'fulfillment';

  const progress = useReportLoaderProgress({
    config,
    isDataReady,
    onComplete,
    periodCount,
    useAssetPeriods,
  });

  return (
    <ReportLoaderDisplay
      currentStep={progress.currentStep}
      completedSteps={progress.completedSteps}
      stepLabels={config.stepLabels}
    />
  );
}
